/**
 * Frontend authentication context.
 *
 * Provides the JWT and its locally-decoded payload to the React tree, plus
 * `login` / `logout` actions that wrap `ApiClient.login` and the token
 * storage helpers. The decode is purely cosmetic (used for `isAuthenticated`
 * and to display the current admin's username) - the backend remains the
 * source of truth for signature validity.
 *
 * Validates: Requirements 4.8, 8.2
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ApiClient,
  clearToken,
  getToken,
} from '../api/ApiClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Shape of the JWT payload issued by the backend (`auth.service.js`).
 * `exp` is a Unix timestamp in **seconds** (per RFC 7519), not milliseconds.
 */
export interface JwtPayload {
  sub: string;
  username: string;
  exp: number;
  iat?: number;
}

/**
 * Public surface of the auth context. Consumers should always access this via
 * the `useAuth()` hook.
 */
export interface AuthContextValue {
  token: string | null;
  payload: JwtPayload | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

// ---------------------------------------------------------------------------
// Base64url + JWT decoding helpers (pure, no signature verification)
// ---------------------------------------------------------------------------

/**
 * Decodes a base64url-encoded string. Returns `null` on malformed input.
 *
 * Internal helper; exported only for testing.
 */
export function base64UrlDecode(input: string): string | null {
  if (typeof input !== 'string' || input.length === 0) return null;
  try {
    let s = input.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = s.length % 4;
    if (padLen === 2) s += '==';
    else if (padLen === 3) s += '=';
    else if (padLen !== 0) return null;

    // `atob` produces a binary string; convert to bytes and UTF-8 decode so
    // multibyte characters (e.g. Arabic in `username`) survive intact.
    const binary = atob(s);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return null;
  }
}

/**
 * Splits a JWT by `.`, base64url-decodes the middle segment, and parses it as
 * JSON. Returns `null` for any token that is not three parts, has a
 * non-base64url payload, or whose payload is not a JSON object.
 *
 * This is a *local* decode only - it does not verify the signature.
 *
 * Validates: Requirements 4.8, 8.2 (used by `isAuthenticated` exp check)
 */
export function decodeJwtPayload(token: string | null | undefined): JwtPayload | null {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  const decoded = base64UrlDecode(parts[1]);
  if (decoded === null) return null;

  try {
    const parsed: unknown = JSON.parse(decoded);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Returns true iff `payload` has a numeric `exp` strictly greater than the
 * current wall-clock time in seconds. A missing / non-numeric `exp` is treated
 * as already-expired so that malformed tokens do not grant access.
 */
function isPayloadFresh(payload: JwtPayload | null): boolean {
  if (payload === null) return false;
  if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) return false;
  const nowSeconds = Date.now() / 1000;
  return payload.exp > nowSeconds;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * `AuthProvider` initialises its state from the token stored in
 * `localStorage` (read once on mount) and exposes `login` / `logout` actions
 * that keep both the storage and the in-memory state in sync.
 *
 * Must be rendered inside a `<BrowserRouter>` / `<MemoryRouter>` because
 * `logout` uses `useNavigate` to redirect to `/admin/login`.
 *
 * Validates: Requirements 4.8, 8.2
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const [token, setTokenState] = useState<string | null>(() => getToken());

  // Re-hydrate once on mount in case the initial render happened before
  // localStorage was available (defensive; matches `useState(() => getToken())`).
  useEffect(() => {
    const stored = getToken();
    setTokenState((prev) => (prev === stored ? prev : stored));
  }, []);

  const payload = useMemo<JwtPayload | null>(
    () => (token === null ? null : decodeJwtPayload(token)),
    [token],
  );

  const isAuthenticated = useMemo(
    () => token !== null && isPayloadFresh(payload),
    [token, payload],
  );

  const login = useCallback(
    async (username: string, password: string): Promise<void> => {
      // `ApiClient.login` already persists the token to localStorage on success.
      const result = await ApiClient.login(username, password);
      setTokenState(result.token);
    },
    [],
  );

  const logout = useCallback((): void => {
    clearToken();
    setTokenState(null);
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const value = useMemo<AuthContextValue>(
    () => ({ token, payload, login, logout, isAuthenticated }),
    [token, payload, login, logout, isAuthenticated],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Returns the current `AuthContextValue`. Throws if called outside of an
 * `<AuthProvider>` so misuse surfaces immediately rather than silently
 * yielding `null`.
 */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return value;
}

// Exposed for direct consumption in tests / advanced cases.
export { AuthContext };
